import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, Ruler, Building2, Home as HomeIcon, Sparkles, HeartHandshake, Users } from "lucide-react";
import { PublicLayout } from "@/components/PublicLayout";
import { PropertyCard } from "@/components/PropertyCard";
import { CookieBanner } from "@/components/CookieBanner";
import { HeroImmersive } from "@/components/hero/HeroImmersive";
import { api, mediaUrl } from "@/lib/api";
import { useSeo } from "@/lib/useSeo";

const pillars = [
  { id: "condomini", to: "/amministrazioni-condominiali/", icon: HomeIcon, title: "Amministrazioni Condominiali", badge: "Gestione & Trasparenza", text: "Gestione condominiale trasparente: contabilità, pratiche burocratiche, manutenzione, ripartizione spese proprietario/inquilino, consumi idrici e contabilizzazione del calore." },
  { id: "agenzia", to: "/agenzia-immobiliare-scandicci/", icon: Building2, title: "Agenzia Immobiliare", badge: "Compravendite & Affitti", text: "Compravendite, affitti, valutazioni immobiliari, gestione immobili, verifiche di solvibilità e registrazione contratti." },
  { id: "studio-tecnico", to: "/studio-tecnico/", icon: Ruler, title: "Studio Tecnico", badge: "Progettazione & Catasto", text: "Progettazione, direzione lavori, sicurezza cantieri, stime e perizie, dichiarazioni di successione, visure e planimetrie catastali, energia e ambiente." },
];

const values = [
  { icon: Sparkles, title: "Versatilità", text: "Servizi completi in tre settori chiave: ristrutturazioni, consulenza immobiliare e gestione condominiale, con un approccio attento e specializzato." },
  { icon: HeartHandshake, title: "Principi", text: "Passione e determinazione sono i pilastri del nostro impegno: soluzioni innovative e personalizzate, dalla prima consulenza alla conclusione del progetto." },
  { icon: Users, title: "Autenticità delle persone", text: "Crediamo in relazioni autentiche e sincere: un approccio onesto e trasparente con ogni cliente, partner e collaboratore." },
];

export default function Home() {
  const navigate = useNavigate();
  const { data: featured = [] } = useQuery({ queryKey: ["featured"], queryFn: async () => (await api.get("/properties", { params: { featured: true } })).data });
  const { data: latest = [] } = useQuery({ queryKey: ["home-latest"], queryFn: async () => (await api.get("/properties")).data });
  const { data: posts = [] } = useQuery({ queryKey: ["home-posts"], queryFn: async () => (await api.get("/posts")).data });

  const showProps = (featured.length ? featured : latest).slice(0, 3);

  useSeo({
    title: "Edilstudio Immobiliare Scandicci e Firenze | Agenzia, Studio Tecnico e Amministrazioni Condominiali",
    description: "A Scandicci e Firenze un unico team di fiducia con tutte le competenze professionali: agenzia immobiliare, studio tecnico e amministrazioni condominiali. Dalla compravendita alla gestione del tuo condominio.",
    canonicalPath: "/",
    jsonLd: {
      "@context": "https://schema.org", "@type": "RealEstateAgent", name: "Edilstudio Immobiliare",
      areaServed: ["Scandicci", "Firenze"], address: { "@type": "PostalAddress", streetAddress: "Via Allende 14/c", postalCode: "50018", addressLocality: "Scandicci", addressRegion: "FI", addressCountry: "IT" },
      telephone: "+390555277501", email: "info@edilstudiofi.it", url: typeof window !== "undefined" ? window.location.origin : "",
    },
  });

  return (
    <PublicLayout>
      {/* HERO — immersivo a tutta larghezza */}
      <HeroImmersive />

      {/* THREE PILLARS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20">
        <div className="max-w-2xl">
          <p className="eyebrow">Un unico riferimento</p>
          <h2 className="font-display text-3xl lg:text-4xl font-bold text-[#1B2A41] mt-2">Tre servizi, sotto lo stesso tetto</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6 mt-10">
          {pillars.map((p) => (
            <Link key={p.id} to={p.to} className="group bg-white rounded-2xl p-8 border border-[#DCE6EF] hover:border-[#2472C8]/40 hover:shadow-xl transition-all" data-testid={`pillar-${p.id}`}>
              <div className="w-12 h-12 rounded-xl bg-[#EEF4F9] flex items-center justify-center"><p.icon className="w-6 h-6 text-[#2472C8]" /></div>
              <p className="eyebrow mt-5">{p.badge}</p>
              <h3 className="font-display text-xl font-bold text-[#1B2A41] mt-2">{p.title}</h3>
              <p className="text-[#566476] mt-3 leading-relaxed text-[15px]">{p.text}</p>
              <span className="inline-flex items-center gap-1 text-[#2472C8] font-semibold mt-5 group-hover:gap-2 transition-all">Scopri di più <ArrowRight className="w-4 h-4" /></span>
            </Link>
          ))}
        </div>
      </section>

      {/* FEATURED PROPERTIES */}
      <section className="bg-[#EEF4F9] py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-end justify-between flex-wrap gap-4">
            <div>
              <p className="eyebrow">In vetrina</p>
              <h2 className="font-display text-3xl lg:text-4xl font-bold text-[#1B2A41] mt-2">Immobili selezionati</h2>
            </div>
            <Link to="/immobili" className="text-[#2472C8] font-semibold flex items-center gap-1 hover:gap-2 transition-all" data-testid="home-view-all-properties">Vedi tutti gli immobili <ArrowRight className="w-4 h-4" /></Link>
          </div>
          {showProps.length ? (
            <div className="grid md:grid-cols-3 gap-6 mt-10">
              {showProps.map((p, i) => <PropertyCard key={p.id} property={p} index={i} />)}
            </div>
          ) : (
            <div className="mt-10 bg-white rounded-2xl border border-dashed border-[#CBD7E2] p-12 text-center text-[#8695A6]" data-testid="home-empty-properties">
              Scopri tutte le proposte nella nostra vetrina. Contattaci per immobili in anteprima.
            </div>
          )}
        </div>
      </section>

      {/* CHI SIAMO / VALORI */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20">
        <div className="max-w-2xl">
          <p className="eyebrow">Chi siamo</p>
          <h2 className="font-display text-3xl lg:text-4xl font-bold text-[#1B2A41] mt-2">I valori che guidano il nostro lavoro</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6 mt-10">
          {values.map((v, i) => (
            <div key={i} className="bg-white rounded-2xl p-7 border border-[#DCE6EF]">
              <v.icon className="w-8 h-8 text-[#2472C8]" />
              <h3 className="font-display text-xl font-bold text-[#1B2A41] mt-4">{v.title}</h3>
              <p className="text-[#566476] mt-2 leading-relaxed text-[15px]">{v.text}</p>
            </div>
          ))}
        </div>
        <div className="text-center mt-10">
          <Link to="/chi-siamo" className="inline-flex items-center gap-1 text-[#2472C8] font-semibold hover:gap-2 transition-all">Leggi la nostra storia <ArrowRight className="w-4 h-4" /></Link>
        </div>
      </section>

      {/* BLOG TEASER */}
      {posts.length > 0 && (
        <section className="bg-[#EEF4F9] py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="flex items-end justify-between flex-wrap gap-4">
              <div><p className="eyebrow">Dal blog</p><h2 className="font-display text-3xl lg:text-4xl font-bold text-[#1B2A41] mt-2">Guide e mercato locale</h2></div>
              <Link to="/blog" className="text-[#2472C8] font-semibold flex items-center gap-1 hover:gap-2 transition-all">Tutti gli articoli <ArrowRight className="w-4 h-4" /></Link>
            </div>
            <div className="grid md:grid-cols-3 gap-6 mt-10">
              {posts.slice(0, 3).map((p) => (
                <Link key={p.id} to={`/blog/${p.slug}`} className="bg-white rounded-2xl overflow-hidden border border-[#DCE6EF] hover:shadow-xl transition-shadow group">
                  {p.cover_image && <img src={mediaUrl(p.cover_image)} alt={p.title} className="aspect-video w-full object-cover" />}
                  <div className="p-6">
                    <h3 className="font-heading font-semibold text-[#1B2A41] group-hover:text-[#2472C8] transition-colors">{p.title}</h3>
                    <p className="text-sm text-[#566476] mt-2 line-clamp-2">{p.excerpt}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA BAND */}
      <section className="bg-[#1B2A41] py-16 relative overflow-hidden">
        <div className="absolute -bottom-20 -right-10 w-80 h-80 rounded-full bg-[#2472C8]/20" />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="font-display text-3xl lg:text-4xl font-bold text-white">Lavoriamo insieme al tuo prossimo progetto</h2>
          <p className="text-white/70 mt-3 max-w-xl mx-auto">Il nostro team è qui per aiutarti. Contattaci per una consulenza esperta, senza impegno.</p>
          <div className="flex flex-wrap justify-center gap-3 mt-8">
            <button onClick={() => navigate("/valuta-la-tua-casa")} className="bg-[#F5A623] hover:bg-[#E0961C] text-[#1B2A41] font-bold px-6 py-4 rounded-full transition-colors" data-testid="band-cta-valuta">Valuta la tua casa</button>
            <button onClick={() => navigate("/contact/")} className="bg-white text-[#1B2A41] font-semibold px-6 py-4 rounded-full hover:bg-white/90 transition-colors" data-testid="band-cta-contatti">Contattaci</button>
          </div>
        </div>
      </section>
      <CookieBanner />
    </PublicLayout>
  );
}
